const MB = 1024 * 1024;
export const MAX_ATTACHMENT_BYTES = 25 * MB;
export const VIDEO_ERROR = "Video uploads are not supported in this system.";

export const ACCEPTED_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/csv",
  "text/plain",
];

export const ATTACHMENT_ACCEPT = [
  ".jpg", ".jpeg", ".png", ".webp", ".pdf", ".docx", ".xlsx", ".pptx", ".csv", ".txt",
  ...ACCEPTED_ATTACHMENT_TYPES,
].join(",");

const EXTENSIONS_BY_TYPE = {
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/webp": ["webp"],
  "application/pdf": ["pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ["xlsx"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": ["pptx"],
  "text/csv": ["csv"],
  "text/plain": ["txt"],
};

const BLOCKED_MEDIA_EXTENSIONS = new Set(["mp4", "mov", "avi", "mkv", "webm", "wmv", "mpeg", "mpg"]);
const BLOCKED_EXTENSIONS = new Set([
  ...BLOCKED_MEDIA_EXTENSIONS,
  "svg", "html", "htm", "js", "mjs", "cjs", "exe", "dll", "bat", "cmd", "com", "msi", "sh", "ps1",
  "zip", "rar", "7z", "tar", "gz",
]);

export const formatAttachmentSize = (bytes) => {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  if (value < MB) return `${Math.round(value / 1024)} KB`;
  return `${(value / MB).toFixed(value >= 10 * MB ? 1 : 2)} MB`;
};

export function validateAttachment(file) {
  if (!file) throw new Error("No file was selected.");
  const name = String(file.name || "").trim();
  const extension = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  const mimeType = String(file.type || "").toLowerCase();

  if (mimeType.startsWith("video/") || mimeType.startsWith("audio/") || BLOCKED_MEDIA_EXTENSIONS.has(extension)) {
    throw new Error(VIDEO_ERROR);
  }
  if (!name || !extension || BLOCKED_EXTENSIONS.has(extension)) throw new Error("This file type is not supported.");
  if (!ACCEPTED_ATTACHMENT_TYPES.includes(mimeType)) throw new Error("This file type is not supported.");
  if (!(EXTENSIONS_BY_TYPE[mimeType] || []).includes(extension)) throw new Error("The file extension does not match its content type.");
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error("This file exceeds the 25 MB limit.");
  return { extension, mimeType };
}

const toFile = (blob, name, type) => new File([blob], name, { type, lastModified: Date.now() });
const ratio = (original, stored) => original > 0 ? Math.max(0, Number((((original - stored) / original) * 100).toFixed(1))) : 0;
const baseName = (name) => String(name || "attachment").replace(/\.[^/.]+$/, "").replace(/[^a-z0-9._-]+/gi, "-");

function originalResult(file, method = "original-preserved") {
  return {
    file,
    originalName: file.name,
    finalName: file.name,
    originalSize: file.size,
    storedSize: file.size,
    compressionRatio: 0,
    mimeType: file.type,
    compressionMethod: method,
  };
}

const canvasBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("The image could not be encoded.")), type, quality);
});

async function decodeImage(file) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = "async";
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function compressImage(file) {
  const image = await decodeImage(file);
  try {
    const sourceWidth = image.width || image.naturalWidth;
    const sourceHeight = image.height || image.naturalHeight;
    let scale = Math.min(1920 / sourceWidth, 1920 / sourceHeight, 1);
    let best = null;
    let bestMethod = "";

    for (let resizePass = 0; resizePass < 6; resizePass += 1) {
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d", { alpha: true });
      if (!context) throw new Error("Image optimization is not supported by this browser.");
      context.drawImage(image, 0, 0, width, height);

      for (const quality of [0.72, 0.64, 0.56, 0.48]) {
        const blob = await canvasBlob(canvas, "image/webp", quality);
        if (!best || blob.size < best.size) {
          best = blob;
          bestMethod = `image-webp-${Math.max(width, height)}-q${Math.round(quality * 100)}`;
        }
        if (blob.size <= 900 * 1024) break;
      }
      if (best?.size <= 1.5 * MB) break;
      scale *= 0.82;
    }

    if (!best || best.size >= file.size) return originalResult(file, "original-preserved-larger-output");
    const finalName = `${baseName(file.name)}.webp`;
    const optimized = toFile(best, finalName, "image/webp");
    return {
      file: optimized,
      originalName: file.name,
      finalName,
      originalSize: file.size,
      storedSize: optimized.size,
      compressionRatio: ratio(file.size, optimized.size),
      mimeType: "image/webp",
      compressionMethod: bestMethod,
    };
  } finally {
    if (typeof image.close === "function") image.close();
  }
}

export function detectPdfIntegrity(bytes, fieldObjects, textCharacterCount, sampledPages) {
  const scanBytes = 4 * MB;
  const decoder = new TextDecoder("latin1");
  const source = `${decoder.decode(bytes.slice(0, Math.min(bytes.byteLength, scanBytes)))}\n${decoder.decode(bytes.slice(Math.max(0, bytes.byteLength - scanBytes)))}`;
  if (/\/FT\s*\/Sig|\/Type\s*\/Sig|\/ByteRange\s*\[/.test(source)) return "original-preserved-signed";
  if (fieldObjects && Object.keys(fieldObjects).length) return "original-preserved-form";
  if (sampledPages && textCharacterCount / sampledPages > 500) return "original-preserved-searchable-text";
  return null;
}

export async function compressPdf(file, onProgress = () => {}) {
  onProgress(5);
  const bytes = new Uint8Array(await file.arrayBuffer());
  let pdfjs;
  try {
    pdfjs = await import("pdfjs-dist");
    pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
  } catch {
    return originalResult(file, "original-preserved-processing-unavailable");
  }

  let pdfDocument;
  try {
    // PDF.js transfers its input to the worker; retain the original for signature/integrity checks.
    pdfDocument = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  } catch (error) {
    if (error?.name === "PasswordException") throw new Error("This PDF is password-protected and cannot be processed.");
    throw new Error("This PDF could not be processed safely.");
  }

  try {
    if (pdfDocument.numPages > 75) throw new Error("This PDF exceeds the 75-page optimization limit.");
    const sampledPages = Math.min(pdfDocument.numPages, 3);
    let textCharacterCount = 0;
    for (let pageNumber = 1; pageNumber <= sampledPages; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);
      const text = await page.getTextContent();
      textCharacterCount += text.items.reduce((sum, item) => sum + String(item.str || "").length, 0);
    }
    const fieldObjects = typeof pdfDocument.getFieldObjects === "function" ? await pdfDocument.getFieldObjects() : null;
    const integrityMethod = detectPdfIntegrity(bytes, fieldObjects, textCharacterCount, sampledPages);
    if (integrityMethod) return originalResult(file, integrityMethod);

    const { jsPDF } = await import("jspdf");
    const dpi = file.size > 12 * MB || document.numPages > 40 ? 120 : 140;
    const quality = file.size > 12 * MB ? 0.65 : 0.7;
    let output = null;

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
      const page = await pdfDocument.getPage(pageNumber);
      const points = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: dpi / 72 });
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      const context = canvas.getContext("2d", { alpha: false });
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: context, viewport }).promise;
      const image = await canvasBlob(canvas, "image/jpeg", quality);
      const imageBytes = new Uint8Array(await image.arrayBuffer());
      const orientation = points.width > points.height ? "landscape" : "portrait";
      if (!output) output = new jsPDF({ unit: "pt", format: [points.width, points.height], orientation, compress: true });
      else output.addPage([points.width, points.height], orientation);
      output.addImage(imageBytes, "JPEG", 0, 0, points.width, points.height, undefined, "FAST");
      canvas.width = 1;
      canvas.height = 1;
      onProgress(Math.round(10 + (pageNumber / pdfDocument.numPages) * 80));
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    const blob = output.output("blob");
    if (blob.size >= file.size) return originalResult(file, "original-preserved-larger-output");
    const optimized = toFile(blob, file.name, "application/pdf");
    return {
      file: optimized,
      originalName: file.name,
      finalName: file.name,
      originalSize: file.size,
      storedSize: optimized.size,
      compressionRatio: ratio(file.size, optimized.size),
      mimeType: "application/pdf",
      compressionMethod: `pdf-raster-${dpi}dpi-q${Math.round(quality * 100)}`,
    };
  } finally {
    await pdfDocument.destroy();
  }
}

export async function compressAttachment(file, onProgress) {
  const { mimeType } = validateAttachment(file);
  if (mimeType.startsWith("image/")) return compressImage(file);
  if (mimeType === "application/pdf") return compressPdf(file, onProgress);
  return originalResult(file, "original-preserved-container");
}
